import { MuiMenuItem, TextField, useDebounce } from '@fox/atoms';
import { Pagination, useMediaQuery } from '@mui/material';
import MUIAlert from '@mui/material/Alert';
import { useState } from 'react';
import { PageSizeOptionsWrapper, PaginationWrapper } from '../../NewsList';
import { useContentContext } from '../../content-context';
import { useEvents, useRsvpOptions } from '../query-hooks/query-implementations';
import { CompactView } from './compact-view';
import { EventListContextProvider } from './event-list-context';
import { EventFilters } from './filters';
import { ListView } from './list-view';
import { MobileView } from './mobile-view';

export function EventList({ defaultThumbnailUrl, hideThumbnail }: { defaultThumbnailUrl?: string; hideThumbnail?: boolean }) {
	const { contentId, requestToken } = useContentContext();
	const [page, setSelectedPage] = useState<number>(0);
	const [displayMode, setDisplayMode] = useState<'compact' | 'list'>('compact');
	const pageSizeOptions = [9, 18, 27];
	const [pageSize, setPageSize] = useState<number>(pageSizeOptions[0]);
	const [search, setSearch] = useState<string>(undefined);
	const [startDate, setStartDate] = useState('@today');
	const [endDate, setEndDate] = useState(undefined);
	const [locationFilter, setLocationFilter] = useState<string[]>([]);
	const [stateFilter, setStateFilter] = useState<string[]>([]);
	const [dateFilter, setDateFilter] = useState<string>('All upcoming');
	const [rsvpFilter, setRsvpFilter] = useState<string[]>([]);
	const isMobile = useMediaQuery('(max-width: 800px)');

	const debouncedSearch = useDebounce(search, 500);
	const { data: rsvpOptions, error: rsvpOptionsError, loading: rsvpOptionsLoading } = useRsvpOptions({ contentId, requestToken });

	const { data, error, loading, refetch } = useEvents({
		contentId,
		requestToken,
		search: debouncedSearch,
		startDate,
		endDate,
		page,
		pageSize,
		rsvpOptions: rsvpFilter,
		locations: locationFilter,
		states: stateFilter,
		triggers: ['search', 'startDate', 'endDate', 'page', 'pageSize', 'rsvpOptions', 'locations', 'states']
	});

	if (rsvpOptionsError) return <MUIAlert severity="error">{rsvpOptionsError}</MUIAlert>;
	if (rsvpOptionsLoading) return null;
	return (
		<EventListContextProvider
			dateFilter={dateFilter}
			displayMode={displayMode}
			locationFilter={locationFilter}
			stateFilter={stateFilter}
			rsvpFilter={rsvpFilter}
			rsvpOptions={rsvpOptions}
			search={search}
			setDateFilter={setDateFilter}
			setDisplayMode={setDisplayMode}
			setEndDate={setEndDate}
			setLocationFilter={setLocationFilter}
			setStateFilter={setStateFilter}
			setRsvpFilter={setRsvpFilter}
			setSearch={setSearch}
			setSelectedPage={setSelectedPage}
			setStartDate={setStartDate}
			eventResult={data}
			refetch={refetch}
			defaultThumbnailUrl={defaultThumbnailUrl}
			hideThumbnail={hideThumbnail}
		>
			<div style={{ minHeight: '800px' }}>
				<EventFilters isMobile={isMobile} />
				{loading ? null : error ? (
					<MUIAlert severity="error">{error}</MUIAlert>
				) : (
					<>
						{isMobile ? <MobileView /> : displayMode === 'compact' ? <CompactView /> : <ListView />}
						{data?.total > pageSize ? (
							<PaginationWrapper>
								<Pagination
									style={{ alignSelf: 'center' }}
									count={Math.ceil(data?.total / pageSize)}
									page={page + 1}
									onChange={(_, value) => {
										setSelectedPage(value - 1);
									}}
									color={'secondary'}
								/>
							</PaginationWrapper>
						) : null}
						<PageSizeOptionsWrapper>
							<TextField
								size="small"
								sx={{ width: '150px' }}
								label="Items per page"
								value={pageSize}
								select
								onChange={e => {
									setPageSize(Number(e.target.value));
									setSelectedPage(0);
								}}
							>
								{pageSizeOptions.map(option => (
									<MuiMenuItem value={option} key={option}>
										{option}
									</MuiMenuItem>
								))}
							</TextField>
						</PageSizeOptionsWrapper>
					</>
				)}
			</div>
		</EventListContextProvider>
	);
}
